// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React from 'react';
import { Redirect, Route, Switch } from 'react-router';
import {
  ApiEditPage,
  ApiListPage,
  ApiDetailsPage,
  ApplicationListPage,
  ApplicationDetails,
  ApplicationKeySettings,
  ApiProxyEditPage,
  AuthProviderDetails,
  GatewayBundleListPage,
  OrganizationListPage,
  OrganizationEditPage,
  OrganizationDetailsPage,
  GatewayBundleDetailsPage,
  GatewayBundleEditPage,
  HomePage,
  ProxyDetailsPage,
  UserListPage,
  EulaListPage,
  EulaEditPage,
  EulaDetailsPage,
  CustomPages,
  RateQuotaListPage,
  RateQuotaEditPage,
  RateQuotaDetailsPage,
  PageNotFoundPage,
  UserProfile,
  RequestListPage,
  RequestSettings,
  ProductsDetails,
  ProductsList,
  RegistrationSettings,
  CustomFieldsListPage,
  AuthenticationListPage,
  AuthenticationAddPage,
  CategoryDetailsPage,
  CategoryListPage,
  ApiSettings,
  ResetPassword,
  CheckEmail,
  AccountSetup,
  Registration,
  LoginConfirmation,
  AppearancePage,
  EditAppearancePage,
  Settings,
  Login,
  KeyStoreDetails,
  KeyStoreListPage,
  ApiGroupsList,
  ApiGroupDetails,
  AuditLogs,
  ApiProxyList,
  AddOrEditCustomField,
  ApiPlans,
  ApiPlanDetails,
  EnableApiPlans,
  HLEReport,
  Usage,
  CustomReport,
} from '../pages';
import Dashboard from '../pages/Dashboard';
import UserDetails from '../pages/User/Details';
import ApplicationRequestDetails from '../pages/Requests/AppRequestDetails';
import EmailSettings from '../pages/EmailSettings';
import NewPassword from '../pages/NewPassword';
import ChangePassword from '../pages/ChangePassword';

export default function Routes() {
  return (
    <Switch>
      <Route exact path="/admin/" component={Dashboard} />
      <Route exact path="/admin/login" component={Login} />
      <Route exact path="/admin/app/registration" component={Registration} />
      <Route exact path="/admin/app/check-your-email" component={CheckEmail} />
      <Route exact path="/admin/app/account-setup-form" component={AccountSetup} />
      <Route exact path="/admin/app/reset-password" component={ResetPassword} />
      <Route exact path="/admin/app/home/" component={HomePage} />
      <Route exact path="/admin/app/dashboard/" component={Dashboard} />
      <Route exact path="/admin/app/guest/dashboard/" component={Dashboard} />
      <Route exact path="/admin/saml/login/confirm" component={LoginConfirmation} />

      <Route exact path="/admin/console/applications" component={ApplicationListPage} />
      <Route exact path="/admin/console/applications/add" component={ApplicationDetails} />
      <Route exact path="/admin/console/applications/details/:applicationUuid/" component={ApplicationDetails} />
      <Route exact path="/admin/console/applications/details/:applicationUuid/:tabName" component={ApplicationDetails} />

      <Route exact path="/admin/console/organizations" component={OrganizationListPage} />
      <Route exact path="/admin/console/organizations/add" component={OrganizationEditPage} />
      <Route exact path="/admin/console/organizations/details/:orgUuid" component={OrganizationDetailsPage} />
      <Route exact path="/admin/console/organizations/details/:orgUuid/:tabName" component={OrganizationDetailsPage} />

      <Route exact path="/admin/console/request-settings" component={RequestSettings} />

      <Route exact path="/admin/console/audit-logs" component={AuditLogs} />

      <Route exact path="/admin/console/requests" component={RequestListPage} />
      <Route exact path="/admin/console/requests/:tabName" component={RequestListPage} />
      <Route exact path="/admin/console/requests/details/application/:entityUuid" component={ApplicationRequestDetails} />
      <Route exact path="/admin/console/requests/details/user/:entityUuid" component={ApplicationRequestDetails} />

      <Route exact path="/admin/console/templatecategories/add" component={CategoryDetailsPage} />
      <Route exact path="/admin/console/templatecategories/details/:categoryUuid" component={CategoryDetailsPage} />
      <Route exact path="/admin/console/templatecategories/details/:categoryUuid/:tabName" component={CategoryDetailsPage} />
      <Route exact path="/admin/console/templatecategories" component={CategoryListPage} />

      <Route exact path="/admin/console/users" component={UserListPage} />
      <Route exact path="/admin/console/users/add" component={UserDetails} />
      <Route exact path="/admin/console/users/details/:userUuid" component={UserDetails} />
      <Route exact path="/admin/console/users/details/:userUuid/:tabName" component={UserDetails} />

      <Route exact path="/admin/products/details" component={ProductsDetails} />
      <Route exact path="/admin/products/details/:productUuid" component={ProductsDetails} />
      <Route exact path="/admin/products/details/:productUuid/:tabName" component={ProductsDetails} />
      <Route exact path="/admin/products" component={ProductsList} />
      <Route exact path="/admin/products/:tabName" component={ProductsList} />

      <Route exact path="/admin/key-stores" component={KeyStoreListPage} />
      <Route exact path="/admin/key-stores/details" component={KeyStoreDetails} />
      <Route exact path="/admin/key-stores/details/:uuid" component={KeyStoreDetails} />
      <Route exact path="/admin/key-stores/details/:uuid/:tabName" component={KeyStoreDetails} />

      <Route exact path="/admin/rate-quotas" component={RateQuotaListPage} />
      <Route exact path="/admin/rate-quotas/add/" component={RateQuotaEditPage} />
      <Route exact path="/admin/rate-quotas/details/:rqUuid" component={RateQuotaDetailsPage} />
      <Route exact path="/admin/rate-quotas/details/:rqUuid/:tabName" component={RateQuotaDetailsPage} />
      <Route exact path="/admin/rate-quotas/edit/:rqUuid/" component={RateQuotaEditPage} />

      <Route exact path="/admin/settings" component={Settings} />
      <Route exact path="/admin/settings/custom-pages/" component={CustomPages} />
      <Route exact path="/admin/settings/user-settings" component={RegistrationSettings} />
      <Route exact path="/admin/settings/application-settings" component={ApplicationKeySettings} />
      <Route exact path="/admin/settings/application-settings/:tabName" component={ApplicationKeySettings} />
      <Route exact path="/admin/settings/application-settings/auth-providers/add" component={AuthProviderDetails} />
      <Route exact path="/admin/settings/application-settings/auth-providers/:authProviderUuid/edit/:tabName" component={AuthProviderDetails} />
      <Route exact path="/admin/settings/api-settings" component={ApiSettings} />

      <Route exact path="/admin/user-profile" component={UserProfile} />

      <Route exact path="/admin/api-proxy/" component={ApiProxyList} />
      <Route exact path="/publish/api-proxy/add/" component={ApiProxyEditPage} />
      <Route exact path="/publish/api-proxy/details/:apiProxyUuid/" component={ProxyDetailsPage} />
      <Route exact path="/publish/api-proxy/details/:apiProxyUuid/:tabName" component={ProxyDetailsPage} />
      <Route exact path="/publish/api-proxy/edit/:apiProxyUuid/" component={ApiProxyEditPage} />
      <Route exact path="/publish/api-proxy/edit/:apiProxyUuid/:tabName" component={ApiProxyEditPage} />

      <Route exact path="/publish/apis/" component={ApiListPage} />
      <Route exact path="/publish/apis/add/" component={ApiEditPage} />
      <Route exact path="/publish/apis/details/:apiUuid" component={ApiDetailsPage} />
      <Route exact path="/publish/apis/details/:apiUuid/:tabName" component={ApiDetailsPage} />
      <Route exact path="/publish/apis/edit/:apiUuid/" component={ApiEditPage} />
      <Route exact path="/publish/apis/edit/:apiUuid/:tabName" component={ApiEditPage} />
      <Route exact path="/publish/apis/permissions/:apiUuid" component={ApiEditPage} />
      <Route exact path="/publish/apis/permissions/:apiUuid/:tabName" component={ApiEditPage} />

      <Route exact path="/appearance" component={AppearancePage} />
      <Route exact path="/appearance/global" component={EditAppearancePage} />
      <Route exact path="/appearance/organization/:orgUuid" component={EditAppearancePage} />

      <Route exact path="/admin/settings/email-settings" component={EmailSettings} />
      <Route exact path="/admin/app/new-password" component={NewPassword} />
      <Route exact path="/admin/app/change-password" component={ChangePassword} />

      <Route exact path="/admin/api-groups" component={ApiGroupsList} />
      <Route exact path="/admin/api-groups/add" component={ApiGroupDetails} />
      <Route exact path="/admin/api-groups/add/:tabName" component={ApiGroupDetails} />
      <Route exact path="/admin/api-groups/details/:apiGroupUuid" component={ApiGroupDetails} />
      <Route exact path="/admin/api-groups/details/:apiGroupUuid/:tabName" component={ApiGroupDetails} />
      <Route exact path="/admin/apis/api-plans" component={ApiPlans} />
      <Route exact path="/admin/apis/api-plans/add" component={ApiPlanDetails} />
      <Route exact path="/admin/apis/api-plans/add/:tabName" component={ApiPlanDetails} />
      <Route exact path="/admin/apis/api-plans/details/:apiPlanUuid" component={ApiPlanDetails} />
      <Route exact path="/admin/apis/api-plans/details/:apiPlanUuid/:tabName" component={ApiPlanDetails} />
      <Route exact path="/admin/apis/api-plans/enable" component={EnableApiPlans} />

      <Route
        exact
        path="/publish/applications/add/"
        render={() => (
          <Redirect to={'/admin/console/applications/add'} />
        )}
      />
      <Route
        exact
        path="/publish/applications/details/:applicationUuid/"
        render={({ match }) => (
          <Redirect to={`/admin/console/applications/details/${match.params.applicationUuid}/`} />
        )}
      />
      <Route
        exact
        path="/publish/applications/edit/:applicationUuid/"
        render={({ match }) => (
          <Redirect to={`/admin/console/applications/details/${match.params.applicationUuid}/`} />
        )}
      />
      <Route
        exact
        path="/publish/applications/edit/:applicationUuid/:tabName"
        render={({ match }) => (
          <Redirect to={`/admin/console/applications/details/${match.params.applicationUuid}/`} />
        )}
      />
      <Route
        exact
        path="/dev/applications/details/:applicationUuid/"
        render={({ match }) => (
          <Redirect to={`/admin/console/applications/details/${match.params.applicationUuid}/`} />
        )}
      />

      <Route exact path="/publish/eulas/" component={EulaListPage} />
      <Route exact path="/publish/eulas/add/" component={EulaEditPage} />
      <Route exact path="/publish/eulas/details/:eulaUuid" component={EulaDetailsPage} />
      <Route exact path="/publish/eulas/edit/:eulaUuid/" component={EulaEditPage} />

      <Route exact path="/publish/gateway-bundles" component={GatewayBundleListPage} />
      <Route exact path="/publish/gateway-bundles/add" component={GatewayBundleEditPage} />
      <Route exact path="/publish/gateway-bundles/details/:bundleUuid" component={GatewayBundleDetailsPage} />
      <Route exact path="/publish/gateway-bundles/details/:bundleUuid/:tabName" component={GatewayBundleDetailsPage} />

      <Route exact path="/publish/settings/custom-fields/" component={CustomFieldsListPage} />
      <Route exact path="/publish/settings/custom-fields/add" component={AddOrEditCustomField} />
      <Route exact path="/publish/settings/custom-fields/edit/:uuid" component={AddOrEditCustomField} />

      <Route exact path="/publish/settings/authentication/" component={AuthenticationListPage} />
      <Route exact path="/publish/settings/authentication/add/" component={AuthenticationAddPage} />
      <Route exact path="/publish/settings/authentication/add/:tabName" component={AuthenticationAddPage} />
      <Route exact path="/publish/settings/authentication/edit/:authUuid" component={AuthenticationAddPage} />
      <Route exact path="/publish/settings/authentication/edit/:authUuid/:tabName" component={AuthenticationAddPage} />

      <Route
        exact
        path={'/admin/app/analytics-dashboard'}
        component={HLEReport}
      />
      <Route
        exact
        path={'/admin/app/analytics-dashboard/usage'}
        component={Usage}
      />
      <Route
        exact
        path={'/admin/app/analytics-dashboard/custom-report'}
        component={CustomReport}
      />

      <Route exact path="/404" component={PageNotFoundPage} />
      <Route path="*" component={PageNotFoundPage} />
    </Switch>
  );
}
